import { apiClient } from './apiClient';

/**
 * Internal helper for RPC sticky notes calls
 */
async function rpcStickyNotes<T>(company: string, action: string, payload: any): Promise<T> {
  const { data } = await apiClient.post(`/api/${company}/rpc/sticky-notes/${action}`, payload, {
    withCredentials: true,
  });
  // If backend returns { ok, data }, unwrap it here
  if ((data as any)?.ok && 'data' in data) {
    return (data as any).data as T;
  }
  // If backend returns { ok: true, note } or { ok: true, notes }, return the data directly
  if ((data as any)?.ok) {
    // For responses like { ok: true, note } or { ok: true, notes }
    const { ok, ...rest } = data as any;
    return rest as T;
  }
  return data as T;
}

/**
 * Get all sticky notes for a company
 */
export async function getStickyNotes(company: string) {
  const result = await rpcStickyNotes<{ notes: any[] }>(company, 'list', {});
  return result;
}

/**
 * Get sticky note by ID
 */
export async function getStickyNoteById(company: string, noteId: string) {
  const result = await rpcStickyNotes<{ note: any }>(company, 'get-by-id', { id: noteId });
  return result;
}

/**
 * Create a sticky note
 */
export async function createStickyNote(company: string, payload: any) {
  const result = await rpcStickyNotes<{ note: any }>(company, 'create', payload);
  return result;
}

/**
 * Update a sticky note
 */
export async function updateStickyNote(company: string, noteId: string, payload: any) {
  const result = await rpcStickyNotes<{ note: any }>(company, 'update', {
    id: noteId,
    ...payload,
  });
  return result;
}

/**
 * Delete a sticky note
 */
export async function deleteStickyNote(company: string, noteId: string) {
  await rpcStickyNotes<{ message?: string }>(company, 'delete', { id: noteId });
}

/**
 * Bring sticky note to front
 */
export async function bringStickyNoteToFront(company: string, noteId: string) {
  await rpcStickyNotes<{ note: any }>(company, 'bring-to-front', { id: noteId });
}

/**
 * Batch update sticky notes positions
 */
export async function batchUpdateStickyNotes(company: string, updates: Array<{ id: string; position?: { x: number; y: number }; zIndex?: number }>) {
  await rpcStickyNotes<{ ok: boolean }>(company, 'batch-update', { updates });
}

